<template>
    <section class="flex flex-col min-h-screen items-center justify-center bg-gradient-to-b from-ewhite-50 via-ewhite-100/50 to-white relative overflow-hidden pt-24 pb-16">

        <!-- Decorative Glow Effects -->
        <div class="absolute top-1/4 left-1/10 w-96 h-96 bg-eorange-500/10 rounded-full blur-3xl pointer-events-none z-0"></div>
        <div class="absolute bottom-1/3 right-1/10 w-96 h-96 bg-eblue-500/10 rounded-full blur-3xl pointer-events-none z-0"></div>
        <div class="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:24px_24px] opacity-40 z-0"></div>

        <div class="container mx-auto px-4 md:px-6 relative z-10 grid gap-12 lg:grid-cols-12 items-center">

            <!-- Left Column: Copy & Actions -->
            <div class="lg:col-span-6 flex flex-col items-center lg:items-start text-center lg:text-left space-y-6">
                <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-eorange-50 border border-eorange-200 text-eorange-600 text-xs font-semibold uppercase tracking-wider font-modulus">
                    <span class="w-2 h-2 rounded-full bg-eorange-500 animate-ping"></span>
                    {{ $t('marketing.auto.emanta') }}
                </div>

                <h1 class="text-4xl md:text-5xl xl:text-6xl font-extrabold tracking-tight text-eblack-950 font-modulus-semi-bold leading-tight">
                    {{ $t('welcome_section.first_title_part') }}
                    <span class="text-transparent bg-clip-text bg-gradient-to-r from-eorange-500 to-rajah-600">{{ $t('welcome_section.itineraries') }}</span>
                    {{ $t('welcome_section.second_title_part') }}
                    <span class="text-transparent bg-clip-text bg-gradient-to-r from-eblue-600 to-eblue-400">{{ $t('common.commissions') }}</span>
                    {{ $t('welcome_section.third_title_part') }}
                </h1>

                <p class="text-md md:text-xl font-modulus text-eblack-500 max-w-2xl leading-relaxed">
                    {{ $t('welcome_section.subtitle') }}
                </p>

                <div class="flex flex-col sm:flex-row gap-4 w-full sm:w-auto justify-center lg:justify-start">
                    <a
                    href="#cta-section"
                    class="flex font-modulus items-center justify-center gap-2 text-lg rounded-full px-8 py-3.5 bg-eorange-500 text-white font-semibold hover:bg-eorange-600 hover:scale-105 hover:shadow-lg hover:shadow-eorange-500/25 transition-all duration-300">
                        {{ $t('common.request_demo') }}
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                            <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                    </a>

                    <!-- Simulator Trigger Button (Mobile/Tablet Only) -->
                    <button
                    @click="showSimulatorModal = true"
                    class="flex lg:hidden font-modulus items-center justify-center gap-2 text-lg rounded-full px-8 py-3.5 border border-eorange-500/30 bg-eorange-50 text-eorange-600 font-semibold hover:bg-eorange-100 hover:scale-105 transition-all duration-300">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                        {{ $t('marketing.auto.try_simulator') }}
                    </button>

                    <a
                    href="#features"
                    class="flex font-modulus items-center justify-center gap-2 text-lg rounded-full px-8 py-3.5 border border-ewhite-300 bg-white/60 hover:bg-white text-eblack-800 font-semibold hover:scale-105 transition-all duration-300">
                        {{ $t('messages.about_us') }}
                    </a>
                </div>
            </div>

            <!-- Right Column: Inline Simulator (Desktop Only) -->
            <div class="lg:col-span-6 hidden lg:flex justify-center">
                <div class="w-full max-w-lg bg-white border border-ewhite-200 shadow-2xl rounded-2xl overflow-hidden font-modulus transition-all duration-500 hover:shadow-eorange-500/10">
                    <div class="bg-eblack-950 px-5 py-4 flex items-center justify-between border-b border-eblack-900">
                        <div class="flex items-center gap-2">
                            <div class="flex gap-1.5">
                                <span class="w-3.5 h-3.5 rounded-full bg-red-500/80"></span>
                                <span class="w-3.5 h-3.5 rounded-full bg-yellow-500/80"></span>
                                <span class="w-3.5 h-3.5 rounded-full bg-green-500/80"></span>
                            </div>
                            <span class="text-xs text-white/50 ml-2 font-mono">{{ $t('marketing.auto.emanta_ydts0') }} </span>
                        </div>
                        <div class="text-xs font-semibold text-white bg-eorange-500/20 text-eorange-400 border border-eorange-500/30 px-2.5 py-0.5 rounded-full">
                            {{ $t('welcome_section.widget.title') }}
                        </div>
                    </div>
                    <!-- Simulator Portal Content -->
                    <div class="p-6 space-y-5">
                        <div class="grid grid-cols-2 gap-4">
                            <div class="bg-ewhite-50 p-4 border border-ewhite-200 rounded-xl space-y-1">
                                <span class="text-xs text-eblack-400 uppercase tracking-wider font-semibold">{{ $t('welcome_section.widget.commission_balance') }}</span>
                                <div class="text-2xl font-bold text-eorange-500">
                                    ${{ totalCommission.toFixed(2) }}
                                </div>
                            </div>
                            <div class="bg-ewhite-50 p-4 border border-ewhite-200 rounded-xl flex flex-col justify-between">
                                <span class="text-xs text-eblack-400 uppercase tracking-wider font-semibold">{{ $t('welcome_section.widget.guests') }}</span>
                                <div class="flex items-center justify-between mt-1">
                                    <button @click="adjustGuests(-1)" class="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-ewhite-300 text-eblack-700 hover:border-eorange-500 hover:text-eorange-500 transition-colors focus:outline-none font-bold">-</button>
                                    <span class="text-lg font-bold text-eblack-900">{{ guests }}</span>
                                    <button @click="adjustGuests(1)" class="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-ewhite-300 text-eblack-700 hover:border-eorange-500 hover:text-eorange-500 transition-colors focus:outline-none font-bold">+</button>
                                </div>
                            </div>
                        </div>

                        <div class="space-y-3">
                            <div
                                v-for="activity in activities"
                                :key="activity.id"
                                @click="toggleActivity(activity.id)"
                                :class="[
                                    'p-3.5 border rounded-xl flex items-center justify-between cursor-pointer transition-all duration-300 hover:scale-[1.01]',
                                    isSelected(activity.id) ? 'bg-eorange-50/50 border-eorange-400 shadow-sm' : 'bg-white border-ewhite-200 hover:border-ewhite-300 hover:bg-ewhite-50/30'
                                ]"
                            >
                                <div class="flex items-center gap-3">
                                    <div :class="['w-9 h-9 rounded-lg flex items-center justify-center', isSelected(activity.id) ? 'bg-eorange-500 text-white' : 'bg-ewhite-100 text-eblack-600']">
                                        <span class="text-lg">{{ activity.icon }}</span>
                                    </div>
                                    <div>
                                        <h4 class="text-xs font-bold text-eblack-950">{{ $t(activity.nameKey) }}</h4>
                                        <p class="text-[10px] text-eblack-400 mt-0.5">{{ $t(activity.priceLabel) }} • <span class="text-eorange-500 font-semibold">{{ $t(activity.commissionKey) }}</span></p>
                                    </div>
                                </div>
                                <button :class="['px-2.5 py-1 text-[10px] rounded-full border font-bold transition-all duration-300', isSelected(activity.id) ? 'bg-eorange-500 border-eorange-500 text-white' : 'bg-white border-ewhite-300 text-eblack-700 hover:border-eorange-400 hover:text-eorange-500']">
                                    {{ isSelected(activity.id) ? $t('welcome_section.widget.added') : $t('welcome_section.widget.add_to_itinerary') }}
                                </button>
                            </div>
                        </div>

                        <div class="border-t border-ewhite-200 pt-4">
                            <span class="text-xs text-eblack-400 uppercase tracking-wider font-semibold block mb-2">{{ $t('welcome_section.widget.active_itinerary') }}</span>
                            <div v-if="selectedActivities.length === 0" class="text-xs text-eblack-400 italic py-3 text-center bg-ewhite-50 rounded-xl border border-dashed border-ewhite-300">
                                {{ $t('welcome_section.widget.empty_itinerary') }}
                            </div>
                            <div v-else class="space-y-1.5 max-h-24 overflow-y-auto pr-1">
                                <div v-for="id in selectedActivities" :key="'itin-d-' + id" class="flex justify-between items-center bg-ewhite-50 border border-ewhite-200 px-3 py-1.5 rounded-lg text-[11px]">
                                    <span class="font-bold text-eblack-800">{{ $t(activities.find(a => a.id === id).nameKey) }}</span>
                                    <span class="text-eblack-400 font-semibold">x{{ guests }} {{ $t('welcome_section.widget.guests').toLowerCase() }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Confirm Button -->
                        <div class="pt-2 border-t border-ewhite-200">
                            <button
                                @click="confirmItinerary"
                                :disabled="selectedActivities.length === 0"
                                class="w-full py-2.5 rounded-xl bg-eorange-500 text-white font-bold text-sm hover:bg-eorange-600 transition-colors shadow-md shadow-eorange-500/20 active:scale-95 disabled:opacity-50 disabled:pointer-events-none cursor-pointer"
                            >
                                {{ $t('marketing.auto.confirm_itinerary') }}
                            </button>
                            <p class="text-[9px] text-center text-eblack-400 mt-2 font-medium italic select-none">
                                ⚠️ {{ $t('marketing.auto.demo_disclaimer') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <!-- Mobile/Tablet Simulator Modal -->
        <div
            v-if="showSimulatorModal"
            class="fixed inset-0 bg-eblack-950/70 backdrop-blur-sm z-[999999] flex items-center justify-center p-4 animate-fade-in"
            @click.self="showSimulatorModal = false"
        >
            <div class="relative w-full max-w-lg bg-white border border-ewhite-200 shadow-2xl rounded-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scale-up">

                <!-- Modal Close Button -->
                <button
                    @click="showSimulatorModal = false"
                    class="absolute top-3.5 right-4 text-white hover:text-eorange-400 transition-colors z-[1000000]"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <!-- Modal App Header Mockup -->
                <div class="bg-eblack-950 px-5 py-4 flex items-center justify-between border-b border-eblack-900 shrink-0">
                    <div class="flex items-center gap-2">
                        <div class="flex gap-1.5">
                            <span class="w-3.5 h-3.5 rounded-full bg-red-500/80"></span>
                            <span class="w-3.5 h-3.5 rounded-full bg-yellow-500/80"></span>
                            <span class="w-3.5 h-3.5 rounded-full bg-green-500/80"></span>
                        </div>
                        <span class="text-xs text-white/50 ml-2 font-mono">{{ $t('marketing.auto.mobile_app_label') }}</span>
                    </div>
                </div>

                <!-- Modal Simulator Content -->
                <div class="p-6 space-y-5 overflow-y-auto font-modulus">
                    <div class="grid grid-cols-2 gap-4">
                        <div class="bg-ewhite-50 p-4 border border-ewhite-200 rounded-xl space-y-1">
                            <span class="text-xs text-eblack-400 uppercase tracking-wider font-semibold">{{ $t('welcome_section.widget.commission_balance') }}</span>
                            <div class="text-2xl font-bold text-eorange-500">
                                ${{ totalCommission.toFixed(2) }}
                            </div>
                        </div>
                        <div class="bg-ewhite-50 p-4 border border-ewhite-200 rounded-xl flex flex-col justify-between">
                            <span class="text-xs text-eblack-400 uppercase tracking-wider font-semibold">{{ $t('welcome_section.widget.guests') }}</span>
                            <div class="flex items-center justify-between mt-1">
                                <button @click="adjustGuests(-1)" class="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-ewhite-300 text-eblack-700 hover:border-eorange-500 hover:text-eorange-500 transition-colors focus:outline-none font-bold">-</button>
                                <span class="text-lg font-bold text-eblack-900">{{ guests }}</span>
                                <button @click="adjustGuests(1)" class="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-ewhite-300 text-eblack-700 hover:border-eorange-500 hover:text-eorange-500 transition-colors focus:outline-none font-bold">+</button>
                            </div>
                        </div>
                    </div>

                    <div class="space-y-3">
                        <div
                            v-for="activity in activities"
                            :key="'modal-' + activity.id"
                            @click="toggleActivity(activity.id)"
                            :class="[
                                'p-3.5 border rounded-xl flex items-center justify-between cursor-pointer transition-all duration-300',
                                isSelected(activity.id) ? 'bg-eorange-50/50 border-eorange-400 shadow-sm' : 'bg-white border-ewhite-200 hover:border-ewhite-300 hover:bg-ewhite-50/30'
                            ]"
                        >
                            <div class="flex items-center gap-3">
                                <div :class="['w-9 h-9 rounded-lg flex items-center justify-center', isSelected(activity.id) ? 'bg-eorange-500 text-white' : 'bg-ewhite-100 text-eblack-600']">
                                    <span class="text-lg">{{ activity.icon }}</span>
                                </div>
                                <div>
                                    <h4 class="text-xs font-bold text-eblack-950">{{ $t(activity.nameKey) }}</h4>
                                    <p class="text-[10px] text-eblack-400 mt-0.5">{{ $t(activity.priceLabel) }} • <span class="text-eorange-500 font-semibold">{{ $t(activity.commissionKey) }}</span></p>
                                </div>
                            </div>
                            <button :class="['px-2.5 py-1 text-[10px] rounded-full border font-bold transition-all duration-300', isSelected(activity.id) ? 'bg-eorange-500 border-eorange-500 text-white' : 'bg-white border-ewhite-300 text-eblack-700 hover:border-eorange-400 hover:text-eorange-500']">
                                {{ isSelected(activity.id) ? $t('welcome_section.widget.added') : $t('welcome_section.widget.add_to_itinerary') }}
                            </button>
                        </div>
                    </div>

                    <div class="border-t border-ewhite-200 pt-4">
                        <span class="text-xs text-eblack-400 uppercase tracking-wider font-semibold block mb-2">{{ $t('welcome_section.widget.active_itinerary') }}</span>
                        <div v-if="selectedActivities.length === 0" class="text-xs text-eblack-400 italic py-3 text-center bg-ewhite-50 rounded-xl border border-dashed border-ewhite-300">
                            {{ $t('welcome_section.widget.empty_itinerary') }}
                        </div>
                        <div v-else class="space-y-1.5 max-h-24 overflow-y-auto pr-1">
                            <div v-for="id in selectedActivities" :key="'itin-m-' + id" class="flex justify-between items-center bg-ewhite-50 border border-ewhite-200 px-3 py-1.5 rounded-lg text-[11px]">
                                <span class="font-bold text-eblack-800">{{ $t(activities.find(a => a.id === id).nameKey) }}</span>
                                <span class="text-eblack-400 font-semibold">x{{ guests }} {{ $t('welcome_section.widget.guests').toLowerCase() }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Modal Confirm Button -->
                    <div class="pt-2 border-t border-ewhite-200">
                        <button
                            @click="confirmItinerary"
                            :disabled="selectedActivities.length === 0"
                            class="w-full py-3 rounded-xl bg-eorange-500 text-white font-bold text-sm hover:bg-eorange-600 transition-colors shadow-md shadow-eorange-500/20 active:scale-95 disabled:opacity-50 disabled:pointer-events-none cursor-pointer"
                        >
                            {{ $t('marketing.auto.confirm_itinerary') }}
                        </button>
                        <p class="text-[9px] text-center text-eblack-400 mt-2 font-medium italic select-none">
                            ⚠️ {{ $t('marketing.auto.demo_disclaimer') }}
                        </p>
                    </div>
                </div>

            </div>
        </div>

        <!-- Decorative Bottom wave -->
        <svg
        viewBox="0 0 2 1"
        preserveAspectRatio="none"
        class="absolute bottom-0 left-0 w-full h-12 z-0 pointer-events-none">
            <defs>
                <path id="w"
                d="
                m0 1v-.5
                q.5.5 1 0
                t1 0 1 0 1 0
                v.5z" />
            </defs>
            <g>
            <use href="#w" y=".0" fill="#fac9ae" opacity="0.3" />
            <use href="#w" y=".1" fill="#f3764a" opacity="0.2" />
            <use href="#w" y=".2" fill="#f7a27a" opacity="0.4" />
            </g>
        </svg>
    </section>
</template>

<script>
import { useToast } from 'vue-toastification';

export default {
    name: 'WelcomeSection',
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            showSimulatorModal: false,
            guests: 2,
            selectedActivities: ['activity_1'],
            activities: [
                {
                    id: 'activity_1',
                    nameKey: 'welcome_section.widget.activities.activity_1.name',
                    price: 65,
                    commissionRate: 0.15,
                    commissionKey: 'welcome_section.widget.activities.activity_1.commission',
                    priceLabel: 'welcome_section.widget.activities.activity_1.price',
                    icon: '🌴'
                },
                {
                    id: 'activity_2',
                    nameKey: 'welcome_section.widget.activities.activity_2.name',
                    price: 90,
                    commissionRate: 0.15,
                    commissionKey: 'welcome_section.widget.activities.activity_2.commission',
                    priceLabel: 'welcome_section.widget.activities.activity_2.price',
                    icon: '🌋'
                },
                {
                    id: 'activity_3',
                    nameKey: 'welcome_section.widget.activities.activity_3.name',
                    price: 85,
                    commissionRate: 0.15,
                    commissionKey: 'welcome_section.widget.activities.activity_3.commission',
                    priceLabel: 'welcome_section.widget.activities.activity_3.price',
                    icon: '⛵'
                }
            ]
        };
    },
    computed: {
        totalCommission() {
            let total = 0;
            this.selectedActivities.forEach(id => {
                const act = this.activities.find(a => a.id === id);
                if (act) {
                    total += act.price * act.commissionRate * this.guests;
                }
            });
            return total;
        }
    },
    methods: {
        toggleActivity(id) {
            const index = this.selectedActivities.indexOf(id);
            if (index > -1) {
                this.selectedActivities.splice(index, 1);
            } else {
                this.selectedActivities.push(id);
            }
        },
        isSelected(id) {
            return this.selectedActivities.includes(id);
        },
        adjustGuests(amount) {
            const newValue = this.guests + amount;
            if (newValue >= 1 && newValue <= 10) {
                this.guests = newValue;
            }
        },
        confirmItinerary() {
            this.toast.success(this.$t('marketing.auto.demo_itinerary_created'));
            this.showSimulatorModal = false; // Close modal if open
            this.selectedActivities = []; // Reset active selections
        }
    }
}
</script>

<style lang="scss" scoped>
    use {
        animation: move-forever 12s linear infinite;
        &:nth-child(2) { animation-duration: 8s; animation-delay: -2s; }
        &:nth-child(1) { animation-duration: 15s; }
    }

    @keyframes move-forever {
        0% { transform: translate(-2px, 0); }
        100% { transform: translate(0px, 0); }
    }

    .animate-fade-in {
        animation: fadeIn 0.25s ease-out forwards;
    }

    .animate-scale-up {
        animation: scaleUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    @keyframes scaleUp {
        from { transform: scale(0.95); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
    }
</style>
